// src/components/Integrations/IntegrationsPage.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Stack } from '@mui/material';
import { setDoc, doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useLocation, useNavigate } from 'react-router-dom';
import { db, functions } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization2 } from '../../contexts/OrganizationContext2';
import BigQueryDetailModal from './BigQueryDetailModal';
import BitlyDetailModal from './BitlyDetailModal';
import IntegrationsGallery from './IntegrationsGallery';
import InstallLookerWizard from './InstallLookerWizard';
import LookerDetailModal from './LookerDetailModal';
import { INTEGRATION_IDS } from './integrationsRegistry';
import { useIntegrationForms } from './useIntegrationForms';

export default function IntegrationsPage() {
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showBigQueryModal, setShowBigQueryModal] = useState(false);
  const [showBitlyModal, setShowBitlyModal] = useState(false);
  const [showLookerModal, setShowLookerModal] = useState(false);
  const [showLookerWizard, setShowLookerWizard] = useState(false);
  const [returnNotice, setReturnNotice] = useState(null);
  const [statusRefreshKey, setStatusRefreshKey] = useState(0);

  const { currentUser } = useAuth();
  const { organization } = useOrganization2();
  const location = useLocation();
  const navigate = useNavigate();

  const currentSearch = location.search;
  const organizationId = organization?.id || organization?.organizationId || null;

  const selectedId = useMemo(() => {
    return selectedIntegration?.id || null;
  }, [selectedIntegration]);

  const bigQueryFormsEnabled =
    showBigQueryModal && selectedId === INTEGRATION_IDS.BIGQUERY;

  const {
    forms: bigQueryForms,
    loading: bigQueryFormsLoading,
    loadingMore: bigQueryFormsLoadingMore,
    error: bigQueryFormsError,
    hasMore: hasMoreBigQueryForms,
    refresh: refreshBigQueryForms,
    loadMore: loadMoreBigQueryForms,
  } = useIntegrationForms({
    organizationId,
    provider: INTEGRATION_IDS.BIGQUERY,
    enabled: bigQueryFormsEnabled,
    pageSize: 100,
    includeProviderConfig: true,
  });

  const refreshStatuses = () => {
    setStatusRefreshKey((prev) => prev + 1);
  };

  const refreshBigQuerySurfaces = () => {
    refreshStatuses();
    refreshBigQueryForms();
  };

  useEffect(() => {
    const params = new URLSearchParams(currentSearch);
    let shouldCleanUrl = false;

    if (params.get('looker') === 'installed') {
      console.log(
        '[IntegrationsPage] Detected looker=installed. ' +
          'Auto-opening wizard.'
      );

      params.delete('looker');
      shouldCleanUrl = true;
      setShowLookerWizard(true);
    }

    const provider = params.get('provider');
    const status = params.get('status');
    const reason = params.get('reason');

    if (provider === INTEGRATION_IDS.BITLY && status) {
      shouldCleanUrl = true;
      params.delete('provider');
      params.delete('status');
      params.delete('reason');

      if (status === 'connected') {
        setReturnNotice({
          severity: 'success',
          message: 'Bitly connected successfully.',
        });
        refreshStatuses();
      } else {
        setReturnNotice({
          severity: 'error',
          message:
            'Bitly connection did not complete' +
            (reason ? ` (${reason}).` : '.'),
        });
      }
    }

    if (shouldCleanUrl) {
      navigate(
        {
          pathname: '/integrations',
          search: params.toString(),
        },
        { replace: true }
      );
    }
  }, [currentSearch, navigate]);

  const handleSelectIntegration = (integration) => {
    console.log('[IntegrationsPage] User selected:', integration);

    setSelectedIntegration(integration);

    if (integration.id === INTEGRATION_IDS.BITLY) {
      setShowBitlyModal(true);
      return;
    }

    if (integration.id === INTEGRATION_IDS.BIGQUERY) {
      setShowBigQueryModal(true);
      return;
    }

    if (integration.id === INTEGRATION_IDS.LOOKER) {
      setShowLookerModal(true);
    }
  };

  const handleCloseBigQueryModal = () => {
    setShowBigQueryModal(false);
    setSelectedIntegration(null);
  };

  const handleCloseBitlyModal = () => {
    setShowBitlyModal(false);
    setSelectedIntegration(null);
  };

  const handleCloseLookerModal = () => {
    setShowLookerModal(false);
    setSelectedIntegration(null);
  };

  const handleInstallLooker = () => {
    console.log('[IntegrationsPage] User wants to install/update Looker.');
    setShowLookerModal(false);
    setShowLookerWizard(true);
  };

  const handleWizardComplete = async (result) => {
    console.log('[IntegrationsPage] Wizard completed:', result);
    setShowLookerWizard(false);

    if (!organization?.id) {
      console.warn(
        '[IntegrationsPage] Missing org ID. Aborting wizard finalize.'
      );
      return;
    }

    try {
      const lookerRef = doc(
        db,
        'organizations',
        organization.id,
        'integrations',
        'looker'
      );

      await updateDoc(lookerRef, {
        status: 'syncing',
        lastSyncRequested: new Date().toISOString(),
      });

      console.log('[IntegrationsPage] Set Looker status to syncing.');
      refreshStatuses();
    } catch (err) {
      console.error('[IntegrationsPage] Error setting syncing:', err);
    }

    try {
      const picksRef = doc(
        db,
        'organizations',
        organization.id,
        'lookerSettings',
        'picks'
      );

      await setDoc(
        picksRef,
        {
          selectedForms: result.selectedForms,
          selectedFields: result.selectedFields,
          updatedAt: new Date().toISOString(),
          userId: currentUser?.uid || 'unknownUser',
        },
        { merge: true }
      );

      console.log('[IntegrationsPage] Picks stored in Firestore.');
    } catch (err) {
      console.error('[IntegrationsPage] Error storing picks:', err);
    }

    try {
      const createViewsFn = httpsCallable(functions, 'createLookerViews');

      const payload = {
        organizationId: organization.id,
        selectedForms: result.selectedForms,
        selectedFields: result.selectedFields,
      };

      const response = await createViewsFn(payload);
      console.log('[IntegrationsPage] createLookerViews:', response.data);

      const lookerRef = doc(
        db,
        'organizations',
        organization.id,
        'integrations',
        'looker'
      );

      await updateDoc(lookerRef, {
        status: 'active',
        lastSyncSuccess: new Date().toISOString(),
      });

      console.log('[IntegrationsPage] Set Looker status to active.');
      refreshStatuses();
    } catch (err) {
      console.error('[IntegrationsPage] createLookerViews failed:', err);

      const lookerRef = doc(
        db,
        'organizations',
        organization.id,
        'integrations',
        'looker'
      );

      await updateDoc(lookerRef, {
        status: 'error',
        lastSyncError: new Date().toISOString(),
      });

      refreshStatuses();
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={3}>
        {returnNotice && (
          <Alert
            severity={returnNotice.severity}
            onClose={() => setReturnNotice(null)}
          >
            {returnNotice.message}
          </Alert>
        )}

        <IntegrationsGallery
          onSelectIntegration={handleSelectIntegration}
          refreshKey={statusRefreshKey}
        />

        <BitlyDetailModal
          open={showBitlyModal && selectedId === INTEGRATION_IDS.BITLY}
          onClose={handleCloseBitlyModal}
          onChanged={refreshStatuses}
        />

        <BigQueryDetailModal
          open={
            showBigQueryModal &&
            selectedId === INTEGRATION_IDS.BIGQUERY
          }
          onClose={handleCloseBigQueryModal}
          onChanged={refreshBigQuerySurfaces}
          availableForms={bigQueryForms}
          formsLoading={bigQueryFormsLoading}
          formsError={bigQueryFormsError}
          hasMoreForms={hasMoreBigQueryForms}
          loadingMoreForms={bigQueryFormsLoadingMore}
          onLoadMoreForms={loadMoreBigQueryForms}
          onRefreshForms={refreshBigQueryForms}
        />

        <LookerDetailModal
          open={showLookerModal && selectedId === INTEGRATION_IDS.LOOKER}
          onClose={handleCloseLookerModal}
          onInstall={handleInstallLooker}
        />

        <InstallLookerWizard
          open={showLookerWizard}
          onClose={() => setShowLookerWizard(false)}
          onComplete={handleWizardComplete}
        />
      </Stack>
    </Box>
  );
}
